import { create } from 'zustand'

export interface Notification {
  id: string
  title: string
  description?: string
  variant: 'default' | 'success' | 'destructive' | 'warning'
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
}

interface NotificationState {
  notifications: Notification[]
  addNotification: (notification: Omit<Notification, 'id'>) => string
  removeNotification: (id: string) => void
  clearAll: () => void
}

let notificationId = 0

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],

  addNotification: (notification) => {
    const id = `notification-${++notificationId}`
    set((state) => ({
      notifications: [...state.notifications, { ...notification, id }],
    }))
    return id
  },

  removeNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }))
  },

  clearAll: () => {
    set({ notifications: [] })
  },
}))

// Convenience functions for common notification types
export const notify = {
  success: (title: string, description?: string, action?: Notification['action']) => {
    return useNotificationStore.getState().addNotification({
      title,
      description,
      variant: 'success',
      action,
    })
  },

  error: (title: string, description?: string, action?: Notification['action']) => {
    return useNotificationStore.getState().addNotification({
      title,
      description,
      variant: 'destructive',
      duration: 10000, // Errors stay longer
      action,
    })
  },

  warning: (title: string, description?: string, action?: Notification['action']) => {
    return useNotificationStore.getState().addNotification({
      title,
      description,
      variant: 'warning',
      action,
    })
  },

  info: (title: string, description?: string, action?: Notification['action']) => {
    return useNotificationStore.getState().addNotification({
      title,
      description,
      variant: 'default',
      action,
    })
  },

  // Specific notification types for article events
  articleComplete: (articleTitle: string, _articleId: string, onView?: () => void) => {
    return useNotificationStore.getState().addNotification({
      title: 'Article Generation Complete',
      description: `"${articleTitle}" is ready for review`,
      variant: 'success',
      action: onView ? { label: 'View Article', onClick: onView } : undefined,
    })
  },

  articleFailed: (articleTitle: string, error?: string, onRetry?: () => void) => {
    return useNotificationStore.getState().addNotification({
      title: 'Article Generation Failed',
      description: error || `Failed to generate "${articleTitle}"`,
      variant: 'destructive',
      duration: 10000,
      action: onRetry ? { label: 'Retry', onClick: onRetry } : undefined,
    })
  },

  articlePublished: (articleTitle: string, wpUrl?: string) => {
    return useNotificationStore.getState().addNotification({
      title: 'Article Published',
      description: `"${articleTitle}" has been published to WordPress`,
      variant: 'success',
      action: wpUrl ? { label: 'View Post', onClick: () => window.open(wpUrl, '_blank') } : undefined,
    })
  },

  batchComplete: (total: number, failed: number) => {
    const variant = failed > 0 ? 'warning' : 'success'
    return useNotificationStore.getState().addNotification({
      title: 'Batch Processing Complete',
      description: failed > 0
        ? `${total - failed} of ${total} articles completed (${failed} failed)`
        : `All ${total} articles generated successfully`,
      variant,
    })
  },
}
